/**
 * Omniapps Account Service Integration
 * Handles authentication, token management, and app data storage
 */

const ACCOUNT_API_URL = import.meta.env.VITE_ACCOUNT_SERVICE_URL || 'https://account.omniapps.net';
const APP_NAME = import.meta.env.VITE_APP_NAME || 'omniapps-courier';

// Token storage keys
const ACCESS_TOKEN_KEY = 'omniapps_access_token';
const REFRESH_TOKEN_KEY = 'omniapps_refresh_token';
const USER_KEY = 'omniapps_user';

/**
 * Account Service API
 */
class AccountService {
  constructor() {
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    this.user = this.loadUser();
  }

  /**
   * Load user from localStorage
   */
  loadUser() {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Save tokens and user data
   */
  saveAuth(accessToken, refreshToken, user) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.user = user;
    
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear authentication data
   */
  clearAuth() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.accessToken && !!this.refreshToken;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Make authenticated API request with automatic token refresh
   */
  async authenticatedFetch(url, options = {}) {
    const makeRequest = async (token) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response;
    };

    let response = await makeRequest(this.accessToken);

    // If unauthorized, try to refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        response = await makeRequest(this.accessToken);
      } else {
        // Refresh failed, clear auth and redirect to login
        this.clearAuth();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }

    return response;
  }

  /**
   * Register a new user
   */
  async register(email, password, firstName, lastName, phoneNumber = '') {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
        phoneNumber,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    return data;
  }

  /**
   * Login user
   */
  async login(email, password) {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    this.saveAuth(data.accessToken, data.refreshToken, data.user);
    return data.user;
  }

  /**
   * Logout user
   */
  async logout() {
    if (this.refreshToken) {
      try {
        await fetch(`${ACCOUNT_API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    this.clearAuth();
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${ACCOUNT_API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      this.accessToken = data.accessToken;
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Validate current token and get user info
   */
  async validateToken() {
    try {
      const response = await this.authenticatedFetch(`${ACCOUNT_API_URL}/api/auth/me`);
      
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      this.user = data.user;
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      
      return data.user;
    } catch (error) {
      console.error('Token validation error:', error);
      return null;
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Password reset request failed');
    }

    return data;
  }

  /**
   * Update user profile
   */
  async updateProfile(updates) {
    const response = await this.authenticatedFetch(`${ACCOUNT_API_URL}/api/profile/update`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Profile update failed');
    }

    this.user = { ...this.user, ...updates };
    localStorage.setItem(USER_KEY, JSON.stringify(this.user));
    
    return data;
  }

  /**
   * Store app-specific data
   */
  async setAppData(dataKey, dataValue) {
    const response = await this.authenticatedFetch(`${ACCOUNT_API_URL}/api/data`, {
      method: 'POST',
      body: JSON.stringify({
        appName: APP_NAME,
        dataKey,
        dataValue,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to save app data');
    }

    return data;
  }

  /**
   * Retrieve app-specific data
   */
  async getAppData(dataKey) {
    const url = new URL(`${ACCOUNT_API_URL}/api/data`);
    url.searchParams.set('appName', APP_NAME);
    url.searchParams.set('dataKey', dataKey);

    const response = await this.authenticatedFetch(url.toString());

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to retrieve app data');
    }

    const data = await response.json();
    return data.dataValue;
  }

  /**
   * Delete app-specific data
   */
  async deleteAppData(dataKey) {
    const url = new URL(`${ACCOUNT_API_URL}/api/data`);
    url.searchParams.set('appName', APP_NAME);
    url.searchParams.set('dataKey', dataKey);

    const response = await this.authenticatedFetch(url.toString(), {
      method: 'DELETE',
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete app data');
    }

    return data;
  }
}

// Export singleton instance
export default new AccountService();
